import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { Uploader } from 'plupload'

import { API_ENDPOINT, ATTACHMENT_NUMBER_LIMIT, BUILD_NUMBER } from '../../../../../../../../../../config'
import { finishUpload } from '../../../../../../../../../../redux/thunks/multimedia/multimedia'
import { useAppDispatch, useAppSelector } from '../../../../../../../../../../app/hooks'

import { UploadFile } from '../../../../../../../../../../@types/multimedia-types'
import { AttachmentType } from '../../../../../../../../../../@types/types'
import { showMultimediaMessage } from '../../../../../../../../../../redux/reducers/message/messageFeature'
import { trackingOnAttachmentUpload } from '../../../../../../../../../../tracking/tracking'

import {
  progressUploadAttachment,
  startUploadAttachmentRequest,
} from '../../../../../../../../../../redux/reducers/multimedia/multimediaFeature'
import MultimediaButtonContent from '../common/MultimediaButtonContent'
import { getMultimediaButtonContent, MultimediaButtonTypes } from '../common/mediaButtonContentProvider'
import { WebViewMultimediaButton } from './WebViewMultimediaButton'

import styles from '../common/MultimediaButtonCommon.module.css'
import { InitState } from '../../../../../../../../../../redux/reducers/root/initialState'
import { filenameIsValid } from '../../../../../../../../../../services/validation/rules/attachmentFileNameRule'
import { isWebView } from '../../../../../../../../../../utils/navigation'
import { Button } from 'is24-corecss'
import { useFeatureSwitch } from '../../../../../../../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../../../../../../../utils/features/featureSwitches'
import { getDefaultLogger } from '../../../../../../../../../../services/logger/logger'
import {
  createAndTriggerPluploadFileInput,
  FilePreparationErrorHandler,
  hasSafariWebKitFileCopy,
  isSafariWebKit,
} from '../../../utils/fileUploadUtils'

export const MultimediaButton: React.FC<MultimediaButtonTypes> = (props) => {
  const uploaderRef = useRef<Uploader | null>(null)
  const uploaderReadyRef = useRef(false)
  const destroyPendingRef = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const pluploadButtonRef = useRef<HTMLDivElement>(null)
  const dropzoneIdRef = useRef(uuid())
  const attachmentsNumberRef = useRef(0)
  const flowInformationRef = useRef(props.flowInformation)
  const allowedFileTypeRef = useRef(props.allowedFileType)
  const entryModeRef = useRef(props.entryMode)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const dropzoneId = dropzoneIdRef.current
  const { fileTypes, title, Icon } = getMultimediaButtonContent(props.allowedFileType)
  const attachmentsNumber = useAppSelector((state: InitState) => state.multimediaFeature.attachments?.length || 0)
  const isMobile = isWebView() && props.flowInformation.isMobileFlow
  const { isActive: isInlinePicturesEnabled } = useFeatureSwitch(
    FeatureSwitch.PPA_13285_RENT_SALE_MOBILE_NEW_IMAGE_UPLOAD,
    isMobile
  )
  const useControlledFilePicker = isSafariWebKit()
  useEffect(() => {
    attachmentsNumberRef.current = attachmentsNumber
  }, [attachmentsNumber])

  useEffect(() => {
    flowInformationRef.current = props.flowInformation
  }, [props.flowInformation])

  useEffect(() => {
    allowedFileTypeRef.current = props.allowedFileType
  }, [props.allowedFileType])

  useEffect(() => {
    entryModeRef.current = props.entryMode
  }, [props.entryMode])

  const destroyUploader = () => {
    const uploader = uploaderRef.current
    if (!uploader) {
      return
    }

    try {
      uploader.destroy()
    } catch (error) {
      getDefaultLogger().warn('MULTIMEDIA_UPLOADER_DESTROY_FAILED', {
        buildNumber: BUILD_NUMBER,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
    }

    if ((window as any).pluploadInstance === uploader) {
      ;(window as any).pluploadInstance = undefined
    }

    uploaderRef.current = null
    uploaderReadyRef.current = false
    destroyPendingRef.current = false
  }

  useEffect(() => {
    if (uploaderRef.current) {
      return
    }

    const uploader: Uploader = new Uploader({
      runtimes: 'html5,html4',
      browse_button: isMobile || useControlledFilePicker ? pluploadButtonRef.current : buttonRef.current,
      url: API_ENDPOINT + '/anbieten/private-anbieter/inserieren/api/multimedia/upload',
      file_data_name: 'file',
      init: {
        FilesAdded: (up, files) => {
          if (files?.length + attachmentsNumberRef.current >= ATTACHMENT_NUMBER_LIMIT) {
            files.forEach((file) => up.removeFile(file))
            dispatch(showMultimediaMessage({ message: 'attachmentsNumber' }))
            return
          }
          const invalidFileName = files.some((file) => !filenameIsValid(file.name))
          if (invalidFileName) {
            dispatch(showMultimediaMessage({ message: 'attachmentFilename' }))
            files.forEach((file) => {
              up.removeFile(file)
            })
            return
          }

          files.forEach((file: UploadFile) => {
            const attachmentType = file.type?.includes('image') ? AttachmentType.PICTURE : AttachmentType.DOCUMENT
            dispatch(
              startUploadAttachmentRequest({
                attachmentType,
                file,
                openDetailsModalOnAdd: !isInlinePicturesEnabled,
              })
            )
          })
          trackingOnAttachmentUpload(flowInformationRef.current, allowedFileTypeRef.current, entryModeRef.current)
          up.start()
          up.refresh()
        },
        UploadProgress: (up, file) => {
          dispatch(progressUploadAttachment({ fileId: file.id, percent: file.percent }))
        },
        PostInit: () => {
          uploaderReadyRef.current = true
          if (destroyPendingRef.current) {
            destroyUploader()
            return
          }
          uploader.refresh()
        },
        FileUploaded: (up, file, response) => {
          dispatch(finishUpload(file, response))
        },
        Error: (up, error) => {
          getDefaultLogger().warn('MULTIMEDIA_UPLOAD_FAILED', {
            allowedFileType: allowedFileTypeRef.current,
            buildNumber: BUILD_NUMBER,
            entryMode: entryModeRef.current,
            errorCode: error.code,
            errorMessage: error.message,
            fileId: error.file?.id,
            fileName: error.file?.name,
            filePercent: error.file?.percent,
            fileSize: error.file?.size,
            fileStatus: error.file?.status,
            fileType: error.file?.type,
            flowName: flowInformationRef.current.name,
            httpStatus: (error as any).status,
            queuedFileCount: (up as any).files?.length,
            runtime: (up as any).runtime,
            safariWebKitFileCopyApplied: hasSafariWebKitFileCopy(error.file),
            uploadClient: 'plupload',
            uploaderState: (up as any).state,
          })
          if (error.code === -600) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileSize',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else if (error.code === -601) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileType',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else {
            dispatch(showMultimediaMessage({ message: 'attachment' }))
          }
        },
      },
      multi_selection: true,
      drop_element: dropzoneId,
      filters: {
        max_file_size: '50mb',
        mime_types: [
          {
            extensions: props.allowedFileType,
            title: t('wizard.screen.multimedia.supportedFiles', 'Supported Files'),
          },
        ],
      },
      multipart_params: {
        isFloorPlan: props.isFloorPlan,
      },
    })
    uploader.init()
    uploader.refresh()
    uploaderRef.current = uploader

    // Store uploader reference globally for dynamic updates
    ;(window as any).pluploadInstance = uploader

    return () => {
      if (!uploaderRef.current) {
        return
      }

      destroyPendingRef.current = true
      if (uploaderReadyRef.current) {
        destroyUploader()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilePreparationError: FilePreparationErrorHandler = (file, error) => {
    getDefaultLogger().warn('MULTIMEDIA_UPLOAD_FILE_PREPARATION_FAILED', {
      buildNumber: BUILD_NUMBER,
      errorMessage: error instanceof Error ? error.message : String(error),
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      safariWebKitFileCopyApplied: false,
    })
    dispatch(showMultimediaMessage({ message: 'attachment' }))
  }

  const selectFiles = () => {
    if (!uploaderRef.current) return
    createAndTriggerPluploadFileInput(
      {
        accept: props.allowedFileType
          .split(',')
          .map((extension) => `.${extension}`)
          .join(','),
      },
      uploaderRef.current,
      handleFilePreparationError
    )
  }

  return isMobile && props.flowInformation.isMobileFlow ? (
    <div className={'margin-bottom-l'}>
      <WebViewMultimediaButton ref={pluploadButtonRef} dropzoneId={dropzoneId} />
    </div>
  ) : (
    <>
      {useControlledFilePicker && <div ref={pluploadButtonRef} style={{ display: 'none' }} />}
      <Button
        id={dropzoneId}
        ref={useControlledFilePicker ? undefined : buttonRef}
        className={`${styles.button} cursor-pointer align-left border-radius-rounded`}
        onClick={useControlledFilePicker ? selectFiles : undefined}
      >
        <MultimediaButtonContent Icon={Icon} title={title} subtitle={fileTypes} />
      </Button>
    </>
  )
}
